import dayjs from 'dayjs';
import { Customer, SmartSendItemApiModel, TrackingStatus } from '~/lib/api/smartsend/types';
import { ExternalSearch } from '../../lib/consts/ListyConsts';
import { DefaultSmartSendItemState } from '../../lib/context/SmartSendContextModels';
import { createNewSmartSendItem } from '../smartsend/panels/createNewSmartSendItem';
import { AddressDetails, SearchObject } from '~/components/search/algolia/Models';
import { SmartSendProvider } from '~/lib/consts/providers';
import {
  CoverExtra,
  DeliveredDutyPaidExtra,
  DeliveryGuaranteeExtra,
  ExtendedBaseCoverExtra,
  PrintInStoreExtra,
  SignatureExtra,
  SmsExtra
} from '~/lib/consts/extras';
import { GetClaimForOrderLineId, GetRefundForOrderLineId } from '../../lib/cache/ExtendedOrderCache';

function mapAddress(a: AddressDetails): Customer {
  return {
    Name: a.name,
    AddressLine1: a.property,
    AddressLine2: a.street,
    Postcode: a.postcode,
    CountryIsoCode: a.countryIso3,
    CountrySubdivision: a.countrySubdivision,
    County: a.county,
    Email: a.email,
    Organisation: a.organisation,
    Phone: a.telephone,
    Town: a.town,
    Meta: {},
    Provider: 'Parcel2Go',
    PropertyKey: `${a.property}|${a.street}|${a.postcode}|${a.countryIso3}`
  };
}

export function sanitiseSourceForHit(hit: SearchObject): string {
  return hit.clientId === 'smartsend.cs' || hit.clientId === 'smartsend.clientside'
    ? 'SmartSend'
    : sanitiseSource(hit.source);
}

export function sanitiseSource(source: string): string {
  switch (source) {
    case 'OrderProcess:V2':
    case 'OrderProcess:V2.0':
      return 'Order Process';
    default:
      return source;
  }
}

const fallbackSource = {
  Id: 'manual',
  Name: 'Manual'
};

export function ToSmartSendItem(s: SearchObject): SmartSendItemApiModel {
  const ssi = createNewSmartSendItem(s.collectionAddress.countryIso3);
  const paidDate = dayjs.unix(s.paidDate).toDate();

  ssi.Id = s.orderLineId.toString();

  ssi.Type = s.clientId === 'smartsend.cs' || s.clientId === 'smartsend.clientside' ? 'SmartSend' : s.source;

  ssi.Created = paidDate;
  ssi.Recipient = mapAddress(s.deliveryAddress);
  ssi.Sender = mapAddress(s.collectionAddress);
  ssi.Value = s.estimatedValue;
  ssi.SubType = ExternalSearch;
  ssi.SearchTrackingStatus = s.trackingStatus;
  ssi.SearchIsDropOffService = s.service.isDropOffService;

  ssi.Status = 'Active';

  if (s.actionTags?.includes('DISPATCHED')) {
    ssi.MarketplaceStatus = 'Dispatched';
  } else {
    ssi.MarketplaceStatus = 'AwaitingDispatch';
  }

  if (s.actionTags?.includes('PACKAGED')) {
    ssi.Meta['PackagedDate'] = { Type: 'Date', Value: new Date().toString() };
  }

  ssi.Meta['TransactionProcessed'] = { Type: 'Date', Value: ssi.Created.toString() };

  // Extras
  s.surcharges?.forEach((surcharge) => {
    switch (surcharge?.toLowerCase()) {
      case 'print in store':
        ssi.Extras.push({
          Id: PrintInStoreExtra,
          Name: 'Print In Store',
          Provider: SmartSendProvider
        });
        break;

      case 'extended base cover':
        ssi.Extras.push({
          Id: ExtendedBaseCoverExtra,
          Name: 'Extended Base Cover',
          Provider: SmartSendProvider
        });
        break;

      case 'signature':
        ssi.Extras.push({
          Id: SignatureExtra,
          Name: 'Signature',
          Provider: SmartSendProvider
        });
        break;

      case 'delivery alert':
        ssi.Extras.push({
          Id: SmsExtra,
          Name: 'Delivery Alert',
          Provider: SmartSendProvider
        });
        break;

      case 'delivery guarantee':
        ssi.Extras.push({
          Id: DeliveryGuaranteeExtra,
          Name: 'Delivery Guarantee',
          Provider: SmartSendProvider
        });
        break;

      case 'extra comp. cover':
        ssi.Extras.push({
          Id: CoverExtra,
          Name: 'Extra Comp. Cover',
          Provider: SmartSendProvider
        });
        break;

      case 'delivered duty paid':
        ssi.Extras.push({
          Id: DeliveredDutyPaidExtra,
          Name: 'Delivered Duty Paid',
          Provider: SmartSendProvider
        });
        break;
    }
  });

  ssi.BookingDetails = {
    OrderId: s.orderId.toString(),
    OrderLineId: s.orderLineId.toString(),
    Created: paidDate,
    BookingStatus: 'Booked',
    Reference: `${s.prefix}${s.orderLineId}`,
    EstimatedCollectionDate: s.collectionDate ? dayjs.unix(s.collectionDate).toDate() : undefined,
    Service: {
      Id: s.service.slug,
      Name: s.service.name,
      Provider: SmartSendProvider,
      AccountId: SmartSendProvider,
      Meta: {}
    }
  };

  if (s.smartSend.count > 0) {
    ssi.Source = {
      Id: s.smartSend.sources?.[0]?.toLowerCase() || fallbackSource.Id,
      Name: s.smartSend.sources?.[0] || fallbackSource.Name,
      AccountId: null
    };
    ssi.MarketplaceOrderLines = s.smartSend.items.map(
      (x) =>
        ({
          Id: x.id,
          Quantity: 1,
          Meta: {},
          Product: { Description: x.description, Sku: x.sku, Meta: {} } as any,
          Source: { Id: x.source?.toLowerCase() || fallbackSource.Id, Name: x.source || fallbackSource.Name }
        }) as any
    );
  } else {
    ssi.Source = {
      Id: s.source,
      Name: sanitiseSource(s.source),
      AccountId: null
    };
    ssi.MarketplaceOrderLines = [];
  }

  // General Package Dimensions and Consignment
  ssi.PackageDimensions = {
    Weight: s.parcels.reduce((acc, p) => acc + (p.weight || 0), 0),
    Length: s.parcels[0].length || 0,
    Width: s.parcels[0].width || 0,
    Height: s.parcels[0].height || 0
  };

  ssi.Consignment = {
    Parcels: s.parcels.map((p) => ({
      Id: p.id.toString(),
      UID: p.id.toString(),
      CanAdd: false,
      Contents: p.customs?.length
        ? p.customs.map((c) => ({
            Id: c.id.toString(),
            UID: c.id.toString(),
            Description: c.description,
            Quantity: 1,
            Value: 0
          }))
        : [
            {
              Id: p.id.toString(),
              UID: p.id.toString(),
              Description: p.contents,
              Quantity: 1,
              Value: p.estimatedValue
            }
          ],
      Dimensions: {
        Weight: p.weight || 0,
        Length: p.length || 0,
        Width: p.width || 0,
        Height: p.height || 0
      }
    }))
  };

  ssi.Meta['IossCode'] = { Value: s.iOSSCode, Type: 'String' };
  ssi.Meta['ExternalTrackingNumber'] = { Value: s.parcelNumbers[0], Type: 'String' };
  ssi.Tags = [];
  ssi.State = { ...DefaultSmartSendItemState(), state: 'IDLE' };

  // Cached Refunds and Claims
  const refund = GetRefundForOrderLineId(s.orderLineId);
  const claim = GetClaimForOrderLineId(s.orderLineId);

  // Various Order Statuses
  ssi.Statuses = {
    ClaimStatus: claim?.status || (s.hasClaim ? s.claimStatus : undefined),
    RefundStatus: refund?.status || (s.refunded ? 'Refunded' : undefined),
    TrackingStatus: s.trackingStatus as TrackingStatus
  };

  // Claims
  if (s.hasClaim || claim) {
    ssi.Tags.push('CLAIM');
  }

  return ssi;
}
